import React from 'react';
import { Box, SpaceBetween, Icon, IconProps } from '@amzn/awsui-components-core';
import './AttendeePathPicker.scss';

export type AttendeePath = 'upload' | 'clone' | 'manual';

interface AttendeePathPickerProps {
  onSelect: (path: AttendeePath) => void;
  disabledPaths?: Set<AttendeePath>;
  displayName: string;
}

interface PathCard {
  id: AttendeePath;
  icon: IconProps['name'];
  title: string;
  description: string;
  buttonLabel: string;
}

const PATH_CARDS: readonly PathCard[] = [
  {
    id: 'upload',
    icon: 'upload',
    title: 'Smart Upload',
    description: 'Drop any file and AI will extract attendees automatically',
    buttonLabel: 'Upload & Go'
  },
  {
    id: 'clone',
    icon: 'copy',
    title: 'Clone a Meeting',
    description: 'Pick a past meeting or paste a link to import attendees',
    buttonLabel: 'Browse & Import'
  },
  {
    id: 'manual',
    icon: 'add-plus',
    title: 'Add Manually',
    description: 'Search and add attendees one at a time',
    buttonLabel: 'Start Fresh'
  }
] as const;

export const AttendeePathPicker: React.FC<AttendeePathPickerProps> = ({ onSelect, disabledPaths, displayName }) => (
  <div className="path-picker">
    <Box variant="p" color="text-body-secondary" margin={{ bottom: 's' }} fontSize="heading-m" fontWeight="bold">
      👋 <span className="path-picker__greeting">Hey {displayName}, let's set up the meeting for you. How would you like to add attendees?</span>
    </Box>
    <br />
    <div className="path-picker__grid">
      {PATH_CARDS.map((card) => {
        const isDisabled = disabledPaths?.has(card.id) ?? false;
        return (
          <button
            key={card.id}
            type="button"
            className={`path-card${isDisabled ? ' path-card--disabled' : ''}`}
            onClick={() => !isDisabled && onSelect(card.id)}
            disabled={isDisabled}
          >
            <SpaceBetween size="s" direction="vertical" alignItems="center">
              <div className="path-card__icon">
                <Icon name={card.icon} size="big" />
              </div>
              <Box variant="h4" textAlign="center">
                {card.title}
              </Box>
              <Box variant="p" color="text-body-secondary" textAlign="center" fontSize="body-s">
                {card.description}
              </Box>
              <span className="path-card__btn">{card.buttonLabel}</span>
            </SpaceBetween>
          </button>
        );
      })}
    </div>
  </div>
);
